import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react'
import {
  Ticket,
  PriceRule,
  CashSession,
  Payment,
  ParkingLotSettings,
  ParkingSpace,
  SpaceStatus,
  Subscriber,
  SubscriberPayment,
} from '@/lib/types'

interface AppContextType {
  tickets: Ticket[]
  rules: PriceRule[]
  spaces: ParkingSpace[]
  currentSession: CashSession | null
  payments: Payment[]
  subscribers: Subscriber[]
  subscriberPayments: SubscriberPayment[]
  settings: ParkingLotSettings
  isOperationMode: boolean
  checkIn: (ticket: Omit<Ticket, 'id' | 'checkInAt' | 'status'>) => void
  checkOut: (ticketId: string, amount: number, method: string) => void
  updateSpaceStatus: (spaceId: string, status: SpaceStatus) => void
  updateSpace: (spaceId: string, updates: Partial<ParkingSpace>) => void
  generateSpaces: (total: number) => void
  openSession: (amount: number) => void
  closeSession: (cash: number, pix: number, card: number, notes: string) => void
  updateRule: (rule: PriceRule) => void
  updateSettings: (settings: ParkingLotSettings) => void
  toggleOperationMode: () => void
  addSubscriber: (sub: Omit<Subscriber, 'id' | 'createdAt'>) => void
  updateSubscriber: (id: string, updates: Partial<Subscriber>) => void
  addSubscriberPayment: (
    payment: Omit<SubscriberPayment, 'id' | 'paidAt'>,
  ) => void
}

const AppContext = createContext<AppContextType | null>(null)

const mockRules: PriceRule[] = [
  {
    id: '1',
    type: 'Carro',
    firstHour: 10,
    additionalHour: 5,
    dailyCap: 50,
    graceMinutes: 15,
    roundingMode: 'ceil',
    monthlyRate: 250,
    peakMultiplier: 1.5,
    peakStartHour: '18:00',
    peakEndHour: '22:00',
  },
  {
    id: '2',
    type: 'Moto',
    firstHour: 5,
    additionalHour: 3,
    dailyCap: 25,
    graceMinutes: 15,
    roundingMode: 'ceil',
    monthlyRate: 120,
  },
  {
    id: '3',
    type: 'Outros',
    firstHour: 15,
    additionalHour: 8,
    dailyCap: 80,
    graceMinutes: 15,
    roundingMode: 'ceil',
  },
]

const mockTickets: Ticket[] = [
  {
    id: 't1',
    plate: 'ABC-1234',
    type: 'Carro',
    checkInAt: new Date(Date.now() - 3600000),
    status: 'active',
    notes: '',
    operatorName: 'João Operador',
    spaceId: 's1',
  },
  {
    id: 't2',
    plate: 'XYZ-9876',
    type: 'Moto',
    checkInAt: new Date(Date.now() - 7200000),
    status: 'active',
    notes: 'Mensalista',
    operatorName: 'João Operador',
    spaceId: 's6',
    isMonthly: true,
  },
  {
    id: 't3',
    plate: 'PAR-0001',
    type: 'Carro',
    checkInAt: new Date(Date.now() - 86400000),
    checkOutAt: new Date(Date.now() - 82800000),
    status: 'closed',
    price: 10,
    paymentMethod: 'Pix',
    notes: '',
    operatorName: 'Ana Silva',
  },
  {
    id: 't4',
    plate: 'KOO-9999',
    type: 'Carro',
    checkInAt: new Date(Date.now() - 172800000),
    checkOutAt: new Date(Date.now() - 165600000),
    status: 'closed',
    price: 15,
    paymentMethod: 'Cartão',
    notes: '',
    operatorName: 'João Operador',
  },
]

const mockSpaces: ParkingSpace[] = Array.from({ length: 40 }, (_, i) => ({
  id: `s${i + 1}`,
  number: `${i + 1}`,
  type: i < 5 ? 'vip' : i < 15 ? 'moto' : i < 18 ? 'pcd' : 'regular',
  status: 'available',
}))

mockSpaces[0].status = 'occupied'
mockSpaces[0].ticketId = 't1'
mockSpaces[5].status = 'occupied'
mockSpaces[5].ticketId = 't2'
mockSpaces[20].status = 'maintenance'
mockSpaces[21].status = 'reserved'

const mockPayments: Payment[] = [
  {
    id: 'p1',
    ticketId: 't3',
    method: 'Pix',
    amount: 10,
    paidAt: new Date(Date.now() - 82800000),
  },
  {
    id: 'p2',
    ticketId: 't4',
    method: 'Cartão',
    amount: 15,
    paidAt: new Date(Date.now() - 165600000),
  },
]

const mockSubscribers: Subscriber[] = [
  {
    id: 'sub1',
    name: 'Empresa XYZ',
    document: '12.345.678/0001-99',
    phone: '(11) 99999-9999',
    email: 'contato@xyz.com',
    plates: ['ABC-1234', 'DEF-5678'],
    vehicleType: 'Carro',
    monthlyFee: 300,
    dueDate: 5,
    status: 'Ativo',
    createdAt: new Date(),
  },
  {
    id: 'sub2',
    name: 'Maria Silva',
    document: '123.456.789-00',
    phone: '(11) 98888-8888',
    email: 'maria@gmail.com',
    plates: ['XYZ-9876'],
    vehicleType: 'Moto',
    monthlyFee: 120,
    dueDate: 15,
    status: 'Inadimplente',
    createdAt: new Date(),
  },
]

const mockSubscriberPayments: SubscriberPayment[] = [
  {
    id: 'sp1',
    subscriberId: 'sub1',
    amount: 300,
    paidAt: new Date(),
    referenceMonth: new Date().toISOString().slice(0, 7),
    method: 'Pix',
  },
]

const mockSettings: ParkingLotSettings = {
  name: 'Parkko',
  address: 'Avenida Principal, 1000',
  timezone: 'America/Sao_Paulo',
  totalSpots: 40,
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [tickets, setTickets] = useState<Ticket[]>(mockTickets)
  const [rules, setRules] = useState<PriceRule[]>(mockRules)
  const [spaces, setSpaces] = useState<ParkingSpace[]>(mockSpaces)
  const [currentSession, setCurrentSession] = useState<CashSession | null>({
    id: 's1',
    openedAt: new Date(new Date().setHours(8, 0, 0, 0)),
    openingAmount: 100,
    status: 'open',
  })
  const [payments, setPayments] = useState<Payment[]>(mockPayments)
  const [subscribers, setSubscribers] = useState<Subscriber[]>(mockSubscribers)
  const [subscriberPayments, setSubscriberPayments] = useState<
    SubscriberPayment[]
  >(mockSubscriberPayments)
  const [settings, setSettings] = useState<ParkingLotSettings>(mockSettings)
  const [isOperationMode, setIsOperationMode] = useState(false)

  const checkIn = useCallback(
    (data: Omit<Ticket, 'id' | 'checkInAt' | 'status'>) => {
      const newTicket: Ticket = {
        ...data,
        id: Math.random().toString(36).substr(2, 9),
        checkInAt: new Date(),
        status: 'active',
      }
      setTickets((prev) => [newTicket, ...prev])

      if (data.spaceId) {
        setSpaces((prev) =>
          prev.map((s) =>
            s.id === data.spaceId
              ? { ...s, status: 'occupied', ticketId: newTicket.id }
              : s,
          ),
        )
      }
    },
    [],
  )

  const checkOut = useCallback(
    (ticketId: string, amount: number, method: string) => {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? {
                ...t,
                status: 'closed',
                checkOutAt: new Date(),
                price: amount,
                paymentMethod: method,
              }
            : t,
        ),
      )
      if (amount > 0) {
        setPayments((prev) => [
          ...prev,
          {
            id: Math.random().toString(36).substr(2, 9),
            ticketId,
            amount,
            method,
            paidAt: new Date(),
          },
        ])
      }
      setSpaces((prev) =>
        prev.map((s) =>
          s.ticketId === ticketId
            ? { ...s, status: 'available', ticketId: undefined }
            : s,
        ),
      )
    },
    [],
  )

  const updateSpaceStatus = useCallback(
    (spaceId: string, status: SpaceStatus) => {
      setSpaces((prev) =>
        prev.map((s) =>
          s.id === spaceId
            ? {
                ...s,
                status,
                ticketId: status === 'available' ? undefined : s.ticketId,
              }
            : s,
        ),
      )
    },
    [],
  )

  const updateSpace = useCallback(
    (spaceId: string, updates: Partial<ParkingSpace>) => {
      setSpaces((prev) =>
        prev.map((s) => (s.id === spaceId ? { ...s, ...updates } : s)),
      )
    },
    [],
  )

  const generateSpaces = useCallback((total: number) => {
    setSpaces((prev) => {
      return Array.from({ length: total }, (_, i) => {
        const existing = prev[i]
        if (existing) return existing
        return {
          id: `s${i + 1}`,
          number: `${i + 1}`,
          type: 'regular',
          status: 'available',
        }
      })
    })
  }, [])

  const openSession = useCallback((amount: number) => {
    setCurrentSession({
      id: Math.random().toString(36).substr(2, 9),
      openedAt: new Date(),
      openingAmount: amount,
      status: 'open',
    })
  }, [])

  const closeSession = useCallback(
    (cash: number, pix: number, card: number, notes: string) => {
      setCurrentSession((prev) =>
        prev
          ? {
              ...prev,
              status: 'closed',
              closedAt: new Date(),
              declaredCash: cash,
              declaredPix: pix,
              declaredCard: card,
              notes,
            }
          : null,
      )
      setTimeout(() => setCurrentSession(null), 2000)
    },
    [],
  )

  const updateRule = useCallback((rule: PriceRule) => {
    setRules((prev) => prev.map((r) => (r.id === rule.id ? rule : r)))
  }, [])

  const updateSettings = useCallback((newSettings: ParkingLotSettings) => {
    setSettings(newSettings)
  }, [])

  const toggleOperationMode = useCallback(() => {
    setIsOperationMode((prev) => !prev)
  }, [])

  const addSubscriber = useCallback(
    (data: Omit<Subscriber, 'id' | 'createdAt'>) => {
      setSubscribers((prev) => [
        ...prev,
        {
          ...data,
          id: Math.random().toString(36).substr(2, 9),
          createdAt: new Date(),
        },
      ])
    },
    [],
  )

  const updateSubscriber = useCallback(
    (id: string, updates: Partial<Subscriber>) => {
      setSubscribers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      )
    },
    [],
  )

  const addSubscriberPayment = useCallback(
    (data: Omit<SubscriberPayment, 'id' | 'paidAt'>) => {
      setSubscriberPayments((prev) => [
        ...prev,
        {
          ...data,
          id: Math.random().toString(36).substr(2, 9),
          paidAt: new Date(),
        },
      ])
      setSubscribers((prev) =>
        prev.map((s) =>
          s.id === data.subscriberId ? { ...s, status: 'Ativo' } : s,
        ),
      )
    },
    [],
  )

  return (
    <AppContext.Provider
      value={{
        tickets,
        rules,
        spaces,
        currentSession,
        payments,
        settings,
        isOperationMode,
        subscribers,
        subscriberPayments,
        checkIn,
        checkOut,
        updateSpaceStatus,
        updateSpace,
        generateSpaces,
        openSession,
        closeSession,
        updateRule,
        updateSettings,
        toggleOperationMode,
        addSubscriber,
        updateSubscriber,
        addSubscriberPayment,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const context = useContext(AppContext)
  if (!context) throw new Error('useApp must be used within AppProvider')
  return context
}
